import React, { PropTypes } from "react";
import { Field, change as changeFieldValue } from "redux-form";
import { FormattedMessage } from "react-intl";

import { RectangleSpinner } from "../../../../../../common/spinners";
import { Render } from "../../../../../../common";
import { TextInput, StyledCheckboxInput, SelectInput } from "../../../../../../common/form";
import { AGENT, EXTERNAL_ROLES } from "../../../../../../../utils/Roles";
import memberCoverageGuaranteeTypes, { SINGLE, WAIVED } from "./enums/memberCoverageGuaranteeTypes";
import { MEMBER, MEMBER_MULTIPLIER, PRICE_LIST } from "../../../../../admin/guarantees/enums/guaranteeTypes";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import userHasAccess from "../../../../../../../utils/userHasAccess";
import sortGuaranteesByOrder from "../../../../../../../utils/sortGuaranteesByOrder";
import resolveIsAutomaticallyChecked from "./helpers/resolveIsAutomaticallyChecked";
import { NEW } from "./enums/memberCoverageAddTypes";
import DependentAutoUncheckModal from "./DependentAutoUncheckModal";

/**
 * Wraps the generic sortGuaranteesByOrder helper with accessors specific to
 * member coverage guarantees (resolves order/dependsOn via clientGuaranteeConfigs).
 * Returns items with an `indented` boolean flag.
 */
function sortMemberGuaranteesByOrder(guarantees, clientGuaranteeConfigs) {
  if (!guarantees || !guarantees.length) return [];

  const configMap = {};
  (clientGuaranteeConfigs || []).forEach((cfg) => {
    configMap[cfg.guaranteeId] = cfg;
  });

  const sorted = sortGuaranteesByOrder(guarantees, {
    getGuaranteeId: (g) => g.guaranteeId,
    getName: (g) => g.name || "",
    getOrder: (g) => {
      const cfg = configMap[g.guaranteeId];
      return cfg && cfg.guarantee && cfg.guarantee.order != null
        ? cfg.guarantee.order
        : g.guarantee && g.guarantee.order != null
          ? g.guarantee.order
          : null;
    },
    getDependsOn: (g) => {
      const cfg = configMap[g.guaranteeId];
      if (
        cfg &&
        cfg.dependsOnGuaranteeId != null &&
        guarantees.some(otherGuarantee => otherGuarantee.guaranteeId === cfg.dependsOnGuaranteeId)
      )
        return cfg.dependsOnGuaranteeId;
      return null;
    },
  });

  return sorted.map((g, i, arr) => {
    const cfg = configMap[g.guaranteeId];
    let hasDependsOn;
    if (cfg) {
      hasDependsOn = !!cfg.dependsOnGuaranteeId && arr.some(otherGuarantee => otherGuarantee.guaranteeId === cfg.dependsOnGuaranteeId);
    } else {
      hasDependsOn = !!(g.guarantee && g.guarantee.dependsOnGuaranteeId) && arr.some(otherGuarantee => otherGuarantee.guaranteeId === g.guarantee.dependsOnGuaranteeId);
    }
    return { ...g, indented: hasDependsOn };
  });
}

class RenderGuarantees extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      previousSelectedCoverageType: [],
      showDependentModal: false,
      uncheckedDependentGuarantees: [],
    };
    this.forceNullOnEmpty = this.forceNullOnEmpty.bind(this);
    this.toggleSelection = this.toggleSelection.bind(this);
    this.renderPerMemberConfigs = this.renderPerMemberConfigs.bind(this);
    this.renderFormulaConfigs = this.renderFormulaConfigs.bind(this);
    this.isUserAllowedToModifyGuaranteeConfig = this.isUserAllowedToModifyGuaranteeConfig.bind(this);
    this.propagateParentChange = this.propagateParentChange.bind(this);
    this.propagateDrivingGuaranteeChange = this.propagateDrivingGuaranteeChange.bind(this);
    this.propagateParentTypeChange = this.propagateParentTypeChange.bind(this);
    this.propagateDrivingGuaranteeTypeChange = this.propagateDrivingGuaranteeTypeChange.bind(this);
    this.handleCoverageTypeChange = this.handleCoverageTypeChange.bind(this);
    this.closeDependentModal = this.closeDependentModal.bind(this);

    this.isExternalUser = userHasAccess(EXTERNAL_ROLES, true);
  }

  /**
   * Sorts guarantees, applies auto-check logic for mandatory dependent
   * guarantees, then replaces all field entries.
   */
  _populateFields(guarantees, clientGuaranteeConfigs, applyDefaultConfig) {
    const { fields } = this.props;
    fields.removeAll();

    const sorted = sortMemberGuaranteesByOrder(guarantees, clientGuaranteeConfigs);

    sorted.map((guarantee) => {
      if (applyDefaultConfig) {
        const isChecked = resolveIsAutomaticallyChecked(
          guarantee.guaranteeId, guarantee.coverageType, sorted, clientGuaranteeConfigs,
          guarantee, this.props.activeMember || {}, true
        );
        const coverageType = isChecked ? null : WAIVED.value;
        let fieldToPush = {
          ...guarantee,
          checked: isChecked,
          coverageType: coverageType,
        };

        if(!this.isExternalUser) {
          fieldToPush = {...fieldToPush, ageOfPolicyExpiration: null, ageOfPolicyDecrease: null, invoicedClientRate: null, 
            allowance: null, supplierRate: null, volume: null, formulaMspa: null};
        }
        fields.push(fieldToPush);
      } else {
        fields.push({ ...guarantee });
      }
    });
  }

  closeDependentModal() {
    this.setState({ showDependentModal: false, uncheckedDependentGuarantees: [] });
  }

  trackDependentUncheck(guaranteeNames) {
    if (guaranteeNames && guaranteeNames.length > 0) {
      this.setState({
        showDependentModal: true,
        uncheckedDependentGuarantees: guaranteeNames,
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    if((!this.props.guarantees || this.props.guarantees.length === 0) 
      && nextProps.guarantees && nextProps.guarantees.length > 0) {
      /**
       * Initial status
       */
      this._populateFields(nextProps.guarantees, nextProps.clientGuaranteeConfigs);
    }
  }

  componentDidUpdate(prevProps) {
    const { needsRefresh, selectedMemberId, firstMemberSelect } = this.props;

    if(selectedMemberId != prevProps.selectedMemberId) {
      this._populateFields(this.props.guarantees, this.props.clientGuaranteeConfigs);
    }

    else if (firstMemberSelect === true) {
      this._populateFields(this.props.guarantees, this.props.clientGuaranteeConfigs);
      this.props.setFirstMemberSelect(false);
    }
    else if (needsRefresh && prevProps.needsRefresh !== needsRefresh) {
      /**
       * onClassChange, reset data.
       */
      this._populateFields(this.props.guarantees, this.props.clientGuaranteeConfigs, true);
      this.props.setNeedsRefresh(false);
    }
  }


  componentWillUnmount() {
    this.props.fields.removeAll();
  }

  forceNullOnEmpty(value) {
    return value === "" ? null : value;
  }

  isMemberGuarantee(guaranteeType) {
    return [MEMBER.value, MEMBER_MULTIPLIER.value].includes(guaranteeType);
  }

  isUserAllowedToModifyGuaranteeConfig(guaranteeType) {
    return !this.isExternalUser || !this.isMemberGuarantee(guaranteeType);
  }

  /**
   * Resolves the dependsOnGuaranteeId for a given guarantee, checking
   * client-level configs.
   */
  getDependsOnForGuarantee(guaranteeId) {
    const { clientGuaranteeConfigs } = this.props;
    const cfg = (clientGuaranteeConfigs || []).find(
      (c) => c.guaranteeId === guaranteeId
    );
    if (cfg && cfg.dependsOnGuaranteeId != null) return cfg.dependsOnGuaranteeId;
    return null;
  }

  /**
   * When a parent guarantee is changed (checked/unchecked or type changed),
   * propagate to all dependent (child) guarantees in the field array.
   * Auto-checking children is conditioned by isAutomaticallyChecked config.
   */
  propagateParentChange(parentGuaranteeId, newChecked, newCoverageType) {
    const { fields, formName, clientGuaranteeConfigs } = this.props;
    fields.forEach((fieldName, index) => {
      const field = fields.get(index);
      const dependsOn = this.getDependsOnForGuarantee(field.guaranteeId);
      const isDisabled = this.props.readOnly || !this.isUserAllowedToModifyGuaranteeConfig(field.guarantee.guaranteeType);
      const config = (clientGuaranteeConfigs || []).find((c) => c.guaranteeId === field.guaranteeId);
      if (dependsOn === parentGuaranteeId && !isDisabled) {
        // When checking: only auto-check child if isAutomaticallyChecked is enabled
        if (!newChecked) {
            this.props.changeFieldValue(formName, `${fieldName}.checked`, false);
            this.props.changeFieldValue(formName, `${fieldName}.coverageType`, WAIVED.value);

            this.props.changeFieldValue(formName, `${fieldName}.ageOfPolicyExpiration`, null);
            this.props.changeFieldValue(formName, `${fieldName}.ageOfPolicyDecrease`, null);
            this.props.changeFieldValue(formName, `${fieldName}.volume`, null);
            this.props.changeFieldValue(formName, `${fieldName}.formulaMspa`, null);
            this.props.changeFieldValue(formName, `${fieldName}.invoicedClientRate`, null);
            this.props.changeFieldValue(formName, `${fieldName}.allowance`, null);
            this.props.changeFieldValue(formName, `${fieldName}.supplierRate`, null); 
        }
        if (newChecked === true) {
          const activeMember = this.props.activeMember || {};
          const internationalBlocked = activeMember.isInternational && !field.suitableForInternationalMembers;
          if (config.isAutomaticallyChecked && !internationalBlocked) {
            this.props.changeFieldValue(formName, `${fieldName}.coverageType`, null);
          }
          else {
            this.props.changeFieldValue(formName, `${fieldName}.coverageType`, WAIVED.value);
          }
          const autoCheckEnabled = config && config.isAutomaticallyChecked && !internationalBlocked;
          return this.props.changeFieldValue(
            formName,
            `${fieldName}.checked`,
            autoCheckEnabled
          );
        }
        return this.props.changeFieldValue(
          formName,
          `${fieldName}.checked`,
          false
        );
      }
    });
  }

  propagateParentTypeChange(parentGuaranteeId, newChecked, newCoverageType) {
    const { fields, formName } = this.props;
    fields.forEach((fieldName, index) => {
      const field = fields.get(index);
      const dependsOn = this.getDependsOnForGuarantee(field.guaranteeId);
      const isDisabled = this.props.readOnly || !this.isUserAllowedToModifyGuaranteeConfig(field.guarantee.guaranteeType);
      if (field.checked && dependsOn === parentGuaranteeId && !isDisabled) {
        return this.props.changeFieldValue(
          formName,
          `${fieldName}.coverageType`,
          newCoverageType
        );
      }
    });
  }

  /**
   * Returns the set of guaranteeIds whose dependsOnGuaranteeId === parentGuaranteeId.
   * Used to mark guarantees as pending-unchecked before propagateParentChange runs.
   */
  _getChildGuaranteeIds(parentGuaranteeId) {
    const { fields } = this.props;
    const ids = new Set();
    fields.forEach((fieldName, index) => {
      const field = fields.get(index);
      const dependsOn = this.getDependsOnForGuarantee(field.guaranteeId);
      if (dependsOn === parentGuaranteeId) {
        ids.add(field.guaranteeId);
      }
    });
    return ids;
  }

  /**
   * Returns a Map<guaranteeId, newCoverageType> for checked children of parentGuaranteeId
   * that propagateParentTypeChange is about to override.
   * Used so hasEligibleDrivingGuarantee sees the post-propagation coverageType.
   */
  _getChildCoverageTypeOverrides(parentGuaranteeId, newCoverageType) {
    const { fields } = this.props;
    const overrides = new Map();
    fields.forEach((fieldName, index) => {
      const field = fields.get(index);
      const dependsOn = this.getDependsOnForGuarantee(field.guaranteeId);
      const isDisabled =
        this.props.readOnly ||
        !this.isUserAllowedToModifyGuaranteeConfig(field.guarantee.guaranteeType);
      if (field.checked && dependsOn === parentGuaranteeId && !isDisabled) {
        overrides.set(field.guaranteeId, newCoverageType);
      }
    });
    return overrides;
  }

  /**
   * Returns true if, after applying the pending change to changedGuaranteeId,
   * any pending unchecks, and any pending coverageType overrides, at least one
   * driving guarantee will be checked with a non-SINGLE/null/WAIVED coverageType.
   * Called before the Redux state updates.
   */
  hasEligibleDrivingGuarantee(changedGuaranteeId, newChecked, newCoverageType, pendingUncheckedIds = new Set(), pendingCoverageTypeOverrides = new Map()) {
    const { fields } = this.props;
    const allFields = [];
    fields.forEach((fieldName, index) => {
      allFields.push({ fieldName, index, ...fields.get(index) });
    });

    return allFields.some((f) => {
      if (!f.guarantee || !f.guarantee.isDrivingMandatoryDependentGuarantee) {
        return false;
      }
      // Apply in-flight changes: the guarantee that just changed + any pending unchecks that are being propagated by Redux.
      let effectiveChecked =
        f.guaranteeId === changedGuaranteeId ? newChecked : f.checked;
      if (pendingUncheckedIds.has(f.guaranteeId)) effectiveChecked = false;

      // Apply pending coverageType overrides from propagateParentTypeChange
      let effectiveCoverageType =
        f.guaranteeId === changedGuaranteeId ? newCoverageType : f.coverageType;
      if (pendingCoverageTypeOverrides.has(f.guaranteeId)) {
        effectiveCoverageType = pendingCoverageTypeOverrides.get(f.guaranteeId);
      }

      // effectiveChecked already covers the unchecked case (checked=false regardless of coverageType).
      // WAIVED must not be excluded here: a checked driving guarantee with type RENONCÉ (WAIVED)
      // is still eligible and must not cause PAC to be unchecked.
      return effectiveChecked && ![SINGLE.value, null, undefined].includes(effectiveCoverageType);
    });
  }

  propagateDrivingGuaranteeTypeChange(changedGuaranteeId, newChecked, newCoverageType, pendingCoverageTypeOverrides = new Map()) {
    const { fields, formName, clientGuaranteeConfigs } = this.props;

    /** Take all fields, put them into an Array. */
    const allFields = [];
    fields.forEach((fieldName, index) => {
      allFields.push({ fieldName, index, ...fields.get(index) });
    });

    const mandatoryDepGuarantees = allFields.filter(
      (f) => f.guarantee && f.guarantee.isMandatoryDependentGuarantee,
    );
    if (!mandatoryDepGuarantees) return;

    const eligible = this.hasEligibleDrivingGuarantee(
      changedGuaranteeId,
      newChecked,
      newCoverageType,
      new Set(),
      pendingCoverageTypeOverrides,
    );

    const uncheckedNames = [];
    mandatoryDepGuarantees.forEach((mdg) => {
      const config = (clientGuaranteeConfigs || []).find(
        (c) => c.guaranteeId === mdg.guaranteeId,
      );
      const autoCheckEnabled = config && config.isAutomaticallyChecked;

      if (eligible) {
        // At least one driving guarantee is non-SINGLE: check PAC only if auto-check enabled
        if (!autoCheckEnabled) return;
        // Do not auto-check a PAC that is not suitable for the international member
        const activeMember = this.props.activeMember || {};
        if (activeMember.isInternational && !mdg.suitableForInternationalMembers) return;
        this.props.changeFieldValue(formName, `${mdg.fieldName}.checked`, true);
      } else {
        // No eligible driving guarantee: always uncheck PAC
        if (mdg.checked) {
          uncheckedNames.push(mdg.name);
        }
        this.props.changeFieldValue(formName, `${mdg.fieldName}.checked`, false);
        this.props.changeFieldValue(
          formName,
          `${mdg.fieldName}.coverageType`,
          WAIVED.value,
        );
        this.props.changeFieldValue(
          formName,
          `${mdg.fieldName}.ageOfPolicyExpiration`,
          null,
        );
        this.props.changeFieldValue(
          formName,
          `${mdg.fieldName}.ageOfPolicyDecrease`,
          null,
        );
        this.props.changeFieldValue(formName, `${mdg.fieldName}.volume`, null);
        this.props.changeFieldValue(formName, `${mdg.fieldName}.formulaMspa`, null);
        this.props.changeFieldValue(formName,`${mdg.fieldName}.invoicedClientRate`, null);
        this.props.changeFieldValue(formName, `${mdg.fieldName}.allowance`, null);
        this.props.changeFieldValue(formName, `${mdg.fieldName}.supplierRate`, null);
      }
    });
    this.trackDependentUncheck(uncheckedNames);
  }

  /**
   * When a driving guarantee is toggled:
   * - On check: do nothing — coverageType is null (≠ SINGLE) so ÉLIGIBLE is guaranteed,
   *   and ÉLIGIBLE means we never uncheck a manually-checked PAC.
   * - On uncheck: if no eligible driving guarantee remains (NON ÉLIGIBLE),
   *   always uncheck all mandatory PAC regardless of isAutomaticallyChecked.
   */
  propagateDrivingGuaranteeChange(changedGuaranteeId, newChecked, newCoverageType, pendingUncheckedIds = new Set()) {
    if (newChecked) return;

    const { fields, formName } = this.props;

    /** Take all fields, put them into an Array. */
    const allFields = [];
    fields.forEach((fieldName, index) => {
      allFields.push({ fieldName, index, ...fields.get(index) });
    });

    const mandatoryDepGuarantees = allFields.filter(
      (f) => f.guarantee && f.guarantee.isMandatoryDependentGuarantee,
    );

    if (!mandatoryDepGuarantees) return;

    const eligible = this.hasEligibleDrivingGuarantee(
      changedGuaranteeId,
      newChecked,
      newCoverageType,
      pendingUncheckedIds,
    );

    // Still ÉLIGIBLE — at least one other driving guarantee is checked & non-SINGLE, do nothing.
    if (eligible) return;

    // NON ÉLIGIBLE — uncheck all mandatory PAC regardless of isAutomaticallyChecked.
    const uncheckedNames = [];
    mandatoryDepGuarantees.forEach((mdg) => {
      if (mdg.checked) {
        uncheckedNames.push(mdg.name);
      }
      this.props.changeFieldValue(formName, `${mdg.fieldName}.checked`, false);
      this.props.changeFieldValue(formName, `${mdg.fieldName}.coverageType`, WAIVED.value);
      this.props.changeFieldValue(formName, `${mdg.fieldName}.ageOfPolicyExpiration`, null);
      this.props.changeFieldValue(formName, `${mdg.fieldName}.ageOfPolicyDecrease`, null);
      this.props.changeFieldValue(formName, `${mdg.fieldName}.volume`, null);
      this.props.changeFieldValue(formName, `${mdg.fieldName}.formulaMspa`, null);
      this.props.changeFieldValue(formName, `${mdg.fieldName}.invoicedClientRate`, null);
      this.props.changeFieldValue(formName, `${mdg.fieldName}.allowance`, null);
      this.props.changeFieldValue(formName, `${mdg.fieldName}.supplierRate`, null);      
    });
    this.trackDependentUncheck(uncheckedNames);
  }

  /**
   * When a parent guarantee's coverageType dropdown changes, propagate
   * the new type value to all dependent guarantees.
   */
  handleCoverageTypeChange(index, guaranteeId, event) {
    const field = this.props.fields.get(index);
    const newCoverageType = event.target.value || null;
    if (field.guarantee) {
      if (field.guarantee.isMaster) {
        // Pre-compute coverageType overrides that propagateParentTypeChange is about to apply
        // so propagateDrivingGuaranteeTypeChange can account for them before Redux updates.
        const pendingCoverageTypeOverrides = this._getChildCoverageTypeOverrides(guaranteeId, newCoverageType);
        this.propagateParentTypeChange(guaranteeId, field.checked, newCoverageType);
        if (field.guarantee.isDrivingMandatoryDependentGuarantee) {
          this.propagateDrivingGuaranteeTypeChange(guaranteeId, field.checked, newCoverageType, pendingCoverageTypeOverrides);
        }
      } else if (field.guarantee.isDrivingMandatoryDependentGuarantee) {
        this.propagateDrivingGuaranteeTypeChange(guaranteeId, field.checked, newCoverageType);
      }
    }
  }

  toggleSelection(index, guaranteeId, event) {
  const matchedGuarantee = this.props.allMemberCoverageGuarantees.find(obj => obj.guaranteeId == guaranteeId);
  const checked = event.target.checked;
  const coverageTypeFieldName = `memberCoverageGuarantees[${index}].coverageType`;
  const ageOfPolicyExpirationFieldName = `memberCoverageGuarantees[${index}].ageOfPolicyExpiration`;
  const ageOfPolicyDecreaseFieldName = `memberCoverageGuarantees[${index}].ageOfPolicyDecrease`;
  const volumeFieldName = `memberCoverageGuarantees[${index}].volume`;
  const formulaMspaFieldName = `memberCoverageGuarantees[${index}].formulaMspa`;
  const invoicedClientRateFieldName = `memberCoverageGuarantees[${index}].invoicedClientRate`;
  const allowanceFieldName = `memberCoverageGuarantees[${index}].allowance`;
  const supplierFieldName = `memberCoverageGuarantees[${index}].supplierRate`;

    const field = this.props.fields.get(index);

    if (!matchedGuarantee) return;
    if (!checked) {
      this.props.changeFieldValue(this.props.formName, coverageTypeFieldName, WAIVED.value);
      if (!this.isExternalUser) {
        this.props.changeFieldValue(this.props.formName, ageOfPolicyExpirationFieldName, null);
        this.props.changeFieldValue(this.props.formName, ageOfPolicyDecreaseFieldName, null);
        this.props.changeFieldValue(this.props.formName, volumeFieldName, null);
        this.props.changeFieldValue(this.props.formName, formulaMspaFieldName, null);
        this.props.changeFieldValue(this.props.formName, invoicedClientRateFieldName, null);
        this.props.changeFieldValue(this.props.formName, allowanceFieldName, null);
        this.props.changeFieldValue(this.props.formName, supplierFieldName, null);

      }
    }
    if (checked && field.coverageType === WAIVED.value) {
      this.props.changeFieldValue(this.props.formName, coverageTypeFieldName, null);
    }
    /**
     * If user unchecks a checkbox, we change to WAIVED type.
     * If user rechecks the checkbox, we set coverageType to null in all cases.
     */
    if (field.guarantee && field.guarantee.isDrivingMandatoryDependentGuarantee) {
      // Pre-compute children that propagateParentChange is about to uncheck so
      // hasEligibleDrivingGuarantee can treat them as already unchecked.
      const pendingUncheckedIds = !checked ? this._getChildGuaranteeIds(guaranteeId) : new Set();
      this.propagateDrivingGuaranteeChange(guaranteeId, checked, checked ? matchedGuarantee.coverageType : null, pendingUncheckedIds);
    }

    this.propagateParentChange(guaranteeId, checked, checked ? null : WAIVED.value);
  }

  renderPerMemberConfigs(guarantee, field) {
    return (
      <div className="guarantee-row extra-config config-fields formula-fields">
        <Field
          id={`${guarantee}.volume`}
          name={`${guarantee}.volume`}
          component={TextInput}
          type="absNumber"
          normalize={this.forceNullOnEmpty}
          label={<FormattedMessage id="memberCoverages.form.volume" />}
          disabled={this.props.readOnly}
          required={!!this.props.volumeRequired}
        />
        <Field
          id={`${guarantee}.invoicedClientRate`}
          name={`${guarantee}.invoicedClientRate`}
          component={TextInput}
          type="absNumber"
          normalize={this.forceNullOnEmpty}
          label={
            <FormattedMessage
              id={`clientCoverages.form.invoicedClientRate.${this.props.ratePeriodType}`}
            />
          }
          disabled={this.props.readOnly}
          required={!!this.props.invoicedClientRateRequired}
        />
        <Field
          id={`${guarantee}.allowance`}
          name={`${guarantee}.allowance`}
          component={TextInput}
          type="absNumber"
          onBlur={field.allowanceBlur.bind(null, `${guarantee}.supplierRate`)}
          normalize={this.forceNullOnEmpty}
          label={
            <FormattedMessage
              id={`memberCoverages.form.allowance.${this.props.ratePeriodType}`}
            />
          }
          disabled={this.props.readOnly}
          required={!!this.props.allowanceRequired}
        />
        <Render for={AGENT}>
          <Field
            id={`${guarantee}.supplierRate`}
            name={`${guarantee}.supplierRate`}
            component={TextInput}
            type="absNumber"
            normalize={this.forceNullOnEmpty}
            label={
              <FormattedMessage
                id={`memberCoverages.form.supplierRate.${this.props.ratePeriodType}`}
              />
            }
            readOnly={true}
          />
        </Render>
      </div>
    );
  }

  renderFormulaConfigs(guarantee) {
    return (
      <div className="guarantee-row extra-config config-fields">
        <Field
          id={`${guarantee}.formulaMspa`}
          name={`${guarantee}.formulaMspa`}
          component={TextInput}
          type="absNumber"
          normalize={this.forceNullOnEmpty}
          label={<FormattedMessage id="memberCoverages.form.formulaMspa" />}
          disabled={this.props.readOnly}
        />
      </div>
    );
  }

  render() {
    if (!this.props.fetched || this.props.guarantees.length != this.props.fields.length) {
      return <RectangleSpinner />;
    }
    if (this.props.fetched && this.props.guarantees.length == 0) {
      return (
        <div className="danger">
          <FormattedMessage id="memberCoverages.form.noGuarantees" />
        </div>
      );
    }

    const fields = this.props.fields.map((fieldName, index, fields) => {
      const field = fields.get(index);
      const guaranteeType = field.guarantee.guaranteeType;
      const isDisabled = this.props.readOnly || !this.isUserAllowedToModifyGuaranteeConfig(guaranteeType);

      return (
        <div key={index} className="guarantee-options">
          <div className={`guarantee-row${field.indented ? " client-guarantee-indented" : ""}`}>
            <div className="guarantee checkbox-dropdown">
              <Field
                id={`${fieldName}.checked`}
                name={`${fieldName}.checked`}
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: field.guaranteeId }}
                label={field.name}
                props={{ onChange: this.toggleSelection.bind(null, index, field.guaranteeId) }}
                disabled={isDisabled}
              />
              {guaranteeType == PRICE_LIST.value &&
                <Field
                  id={`${fieldName}.coverageType`}
                  name={`${fieldName}.coverageType`}
                  component={SelectInput}
                  required={true}
                  options={memberCoverageGuaranteeTypes}
                  disabled={isDisabled || !field.checked}
                  label={<FormattedMessage id="memberCoverages.form.coverageType" />}
                  props={{ onChange: this.handleCoverageTypeChange.bind(null, index, field.guaranteeId) }}
                />
              }
            </div>
            {!this.isExternalUser && (
              <div className="options age-options">
                <Field
                  id={`${fieldName}.ageOfPolicyExpiration`}
                  name={`${fieldName}.ageOfPolicyExpiration`}
                  label={<FormattedMessage id="memberCoverages.form.ageOfPolicyExpiration" />}
                  component={TextInput}
                  type="absNumber"
                  disabled={
                    this.props.readOnly || !field.checked
                  }
                  placeholder={field.placeHolderExpiration}
                  normalize={this.forceNullOnEmpty}
                  required={this.props.ageExpirationRequired}
                />
                <Field
                  id={`${fieldName}.ageOfPolicyDecrease`}
                  name={`${fieldName}.ageOfPolicyDecrease`}
                  label={<FormattedMessage id="memberCoverages.form.ageOfPolicyDecrease" />}
                  component={TextInput}
                  type="absNumber"
                  disabled={
                    this.props.readOnly || !field.checked
                  }
                  placeholder={field.placeHolderDecrease}
                  normalize={this.forceNullOnEmpty}
                  required={this.props.ageDecreaseRequired}
                />
              </div>
            )}
          </div>
          {
            !this.isExternalUser &&
            field.isTypeMember &&
            field.checked &&
            this.renderPerMemberConfigs(fieldName, field)
          }
          {
            !this.isExternalUser &&
            guaranteeType == "FORMULA" &&
            field.checked &&
            this.renderFormulaConfigs(fieldName)
          }
        </div>
      );
    });

    return (
      <div>
        <DependentAutoUncheckModal
          show={this.state.showDependentModal}
          onClose={this.closeDependentModal}
          guaranteeNames={this.state.uncheckedDependentGuarantees}
        />
        {fields}
      </div>
    );
  }
}

RenderGuarantees.propTypes = {
  add: PropTypes.bool,
  fetched: PropTypes.bool,
  initValues: PropTypes.object,
  guarantees: PropTypes.array,
  classId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fields: PropTypes.object,
  firstCoverage: PropTypes.bool,
  meta: PropTypes.object,
  readOnly: PropTypes.bool,
  ratePeriodType: PropTypes.string,
  formName: PropTypes.string,
  changeFieldValue: PropTypes.func,
  allMemberCoverageGuarantees: PropTypes.array,
  volumeRequired: PropTypes.bool,
  invoicedClientRateRequired: PropTypes.bool,
  allowanceRequired: PropTypes.bool,
  supplierRateRequired: PropTypes.bool,
  ageExpirationRequired: PropTypes.bool,
  ageDecreaseRequired: PropTypes.bool,
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  addType: PropTypes.string,
  latestCoverage: PropTypes.object,
  nextCoverage: PropTypes.object,
  initialValues: PropTypes.object,
  clientGuaranteeConfigs: PropTypes.array,
  activeMember: PropTypes.object,
  formClassId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  needsRefresh: PropTypes.bool,
  setNeedsRefresh: PropTypes.func,
  firstMemberSelect: PropTypes.bool,
  setFirstMemberSelect: PropTypes.func
};

RenderGuarantees.defaultProps = {
  clientGuaranteeConfigs: [],
};

function mapStateToProps(state, ownProps) {
  const formName = ownProps.formName;
  const members = state.data && state.data.members || {};
  return {
    allMemberCoverageGuarantees: state.form && state.form[formName] && state.form[formName].values && state.form[formName].values.memberCoverageGuarantees,
    activeMember: ownProps.activeMember !== undefined ? ownProps.activeMember : members && members.active,
    formClassId: state.form && state.form[formName] && state.form[formName].values && state.form[formName].values.classId
  };
}

function mapDispatchToProps(dispatch) {
  return {
    changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(RenderGuarantees);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/members/member/coverage/RenderGuarantees.js